import {
	Anchor,
	Box,
	Button,
	Group,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { IconBrandGoogle, IconLock } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PendingVerificationModal } from "../components/auth/pending-verification-modal";
import {
	OAUTH_ERROR_FALLBACK,
	oauthErrorMessage,
	safeDestination,
} from "../components/auth/signin.logic";
import { SigninBrandPanel } from "../components/auth/signin-brand-panel";
import { authClient } from "../utils/auth-client";

export const Route = createFileRoute("/signin")({
	validateSearch: (search: Record<string, unknown>) => ({
		redirect: typeof search.redirect === "string" ? search.redirect : undefined,
		error: typeof search.error === "string" ? search.error : undefined,
	}),
	component: SigninComponent,
});

const destinationFor = (redirect: string | undefined) =>
	safeDestination(redirect, window.location.origin);

function SigninComponent() {
	const navigate = useNavigate();
	const { redirect, error: oauthError } = Route.useSearch();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(oauthErrorMessage(oauthError));
	const [pendingModal, setPendingModal] = useState(false);

	// Sudah punya sesi (mis. kembali dari Google atau dilempar middleware):
	// terverifikasi → lanjut ke tujuan, belum → tampilkan modal menunggu verifikasi admin.
	useEffect(() => {
		let cancelled = false;
		const checkSession = async () => {
			try {
				const res = await fetch("/api/session");
				if (!res.ok) return;
				const user = (await res.json())?.data?.user;
				if (cancelled || !user) return;
				if (user.emailVerified === true) {
					navigate({ to: destinationFor(redirect), replace: true });
				} else {
					setPendingModal(true);
				}
			} catch (err) {
				console.error("Gagal memeriksa sesi di halaman masuk", err);
			}
		};
		void checkSession();
		return () => {
			cancelled = true;
		};
	}, [navigate, redirect]);

	const handleGoogle = async () => {
		setLoading(true);
		setError("");
		try {
			const result = await authClient.signIn.social({
				provider: "google",
				callbackURL: destinationFor(redirect),
				errorCallbackURL: "/signin",
			});
			if (result?.error) {
				setError(result.error.message || OAUTH_ERROR_FALLBACK);
				setLoading(false);
			}
		} catch (err) {
			console.error("Gagal memulai login Google", err);
			setError(OAUTH_ERROR_FALLBACK);
			setLoading(false);
		}
	};

	return (
		<Box
			style={{
				minHeight: "100vh",
				display: "flex",
				fontFamily: "'Inter', 'Poppins', sans-serif",
			}}
		>
			<PendingVerificationModal
				opened={pendingModal}
				onClose={() => setPendingModal(false)}
			/>

			<SigninBrandPanel />

			{/* ── RIGHT PANEL ──────────────────────────────────────────────── */}
			<Box
				style={{
					flex: "1 1 42%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "var(--mantine-color-body)",
					padding: "48px 32px",
					position: "relative",
				}}
			>
				<Box
					style={{
						position: "absolute",
						inset: 0,
						backgroundImage:
							"radial-gradient(ellipse at 70% 8%, rgba(59,143,255,0.07) 0%, transparent 50%), " +
							"radial-gradient(ellipse at 20% 90%, rgba(52,211,153,0.04) 0%, transparent 45%)",
						pointerEvents: "none",
					}}
				/>
				<Box
					style={{
						position: "absolute",
						inset: 0,
						backgroundImage:
							"linear-gradient(rgba(59,143,255,0.03) 1px, transparent 1px), " +
							"linear-gradient(90deg, rgba(59,143,255,0.03) 1px, transparent 1px)",
						backgroundSize: "44px 44px",
						pointerEvents: "none",
					}}
				/>

				<Box
					style={{
						width: "100%",
						maxWidth: 356,
						position: "relative",
						zIndex: 1,
					}}
				>
					<Box mb={24}>
						<Group gap={8} mb={10}>
							<Box
								style={{
									width: 28,
									height: 28,
									borderRadius: 8,
									background: "rgba(59,143,255,0.12)",
									border: "1px solid rgba(59,143,255,0.2)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<IconLock size={13} color="#3B8FFF" />
							</Box>
							<Text
								style={{
									fontSize: 11,
									fontWeight: 700,
									letterSpacing: "0.1em",
									textTransform: "uppercase",
									color: "#3B8FFF",
								}}
							>
								Selamat datang kembali
							</Text>
						</Group>
						<Title
							order={2}
							style={{
								fontSize: 27,
								fontWeight: 800,
								letterSpacing: "-0.025em",
								marginBottom: 6,
								lineHeight: 1.2,
							}}
						>
							Masuk ke Dashboard
						</Title>
						<Text fz="sm" c="dimmed">
							Masuk menggunakan akun Google yang sudah didaftarkan
							administrator.
						</Text>
					</Box>

					<Paper
						radius="xl"
						p={24}
						style={{
							border: "1px solid var(--mantine-color-default-border)",
							boxShadow:
								"0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.07)",
						}}
					>
						<Stack gap={18}>
							{error && (
								<Box
									role="alert"
									style={{
										padding: "10px 14px",
										background: "var(--mantine-color-red-0)",
										border: "1px solid var(--mantine-color-red-2)",
										borderLeft: "3px solid var(--mantine-color-red-5)",
										borderRadius: 10,
									}}
								>
									<Text fz="xs" c="red.7" fw={500}>
										{error}
									</Text>
								</Box>
							)}

							<Button
								variant="default"
								fullWidth
								size="md"
								radius="xl"
								loading={loading}
								leftSection={<IconBrandGoogle size={17} />}
								style={{
									height: 46,
									fontWeight: 600,
									fontSize: 14,
									border: "1.5px solid var(--mantine-color-default-border)",
									boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
								}}
								onClick={handleGoogle}
							>
								Lanjutkan dengan Google
							</Button>
						</Stack>
					</Paper>

					<Group gap={6} justify="center" mt={20} mb={4}>
						<IconLock size={11} color="var(--mantine-color-dimmed)" />
						<Text fz={11} c="dimmed">
							Koneksi aman · Enkripsi SSL 256-bit
						</Text>
					</Group>

					<Text ta="center" fz={11} c="dimmed" style={{ lineHeight: 1.65 }}>
						Dengan masuk, kamu menyetujui{" "}
						<Anchor fz={11} c="dimmed" style={{ textDecoration: "underline" }}>
							kebijakan privasi
						</Anchor>{" "}
						&{" "}
						<Anchor fz={11} c="dimmed" style={{ textDecoration: "underline" }}>
							ketentuan layanan
						</Anchor>
						.
					</Text>
				</Box>
			</Box>
		</Box>
	);
}
